import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase";
import { renameBand } from "../bandMembershipService";
import { useBandWorkspace } from "../contexts/BandWorkspaceContext";
import { useToast } from "../contexts/ToastContext";
import InviteHistory from "./InviteHistory";
import DowngradeConfirmModal from "./DowngradeConfirmModal";
import { BandTrigger } from "../features/upgrade/useUpgradeModal";

export default function MembersTab({ isDeletingAccount, onSetDeletingAccount }) {
  const navigate = useNavigate();
  const {
    band,
    user,
    tenantContext,
    tier,
    patchBand,
    members: membersCtx,
    invites: invitesCtx,
    songs,
    setlists,
    showBand,
    requestConfirm,
    dismissConfirm,
  } = useBandWorkspace();
  const showToast = useToast();

  const {
    members,
    isMembersLoading,
    membersError,
    editingMemberUid,
    editingMemberName,
    editingMemberRole,
    setEditingMemberName,
    setEditingMemberRole,
    startEdit,
    cancelEdit,
    isSavingMember,
    removingUid,
    handleSaveMember,
    handleRemoveMember,
    handleTransferOwnership,
  } = membersCtx;

  const { invites, invitesError, hasMoreInvites, fetchInvites } = invitesCtx;

  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [renameBandValue, setRenameBandValue] = useState(band.name);
  const [isRenamingBand, setIsRenamingBand] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);

  useEffect(() => {
    setRenameBandValue(band.name);
  }, [band.name]);

  const openBillingPortal = async () => {
    setIsPortalLoading(true);
    try {
      const createPortalSession = httpsCallable(functions, "createPortalSession");
      const result = await createPortalSession({ bandId: tenantContext?.bandId });
      const url = result.data?.url;
      if (url) window.location.href = url;
    } catch (err) {
      console.error("Billing portal error:", err);
    } finally {
      setIsPortalLoading(false);
    }
  };

  const handleRenameBand = async () => {
    if (!tenantContext?.bandId) return;
    setIsRenamingBand(true);
    try {
      const { bandName: newName } = await renameBand({
        bandId: tenantContext.bandId,
        newName: renameBandValue,
        uid: user.uid,
      });
      patchBand({ bandName: newName });
      showToast("Band renamed.");
    } catch (err) {
      showToast(err.message || "Couldn't rename band — please try again.");
    } finally {
      setIsRenamingBand(false);
    }
  };

  return (
    <section className="px-4 py-6">
      {/* Billing management */}
      <div className="mb-4 rounded-md border border-border bg-surface p-4">
        <button
          type="button"
          disabled={isPortalLoading || !user.isOwner}
          title={user.isOwner ? undefined : "Only the band owner can manage billing"}
          onClick={() => {
            if (!user.isOwner) return;
            if (tier.tier === "free") {
              showBand(BandTrigger.INVITE_MEMBER_CLICKED);
              return;
            }
            setShowDowngradeModal(true);
          }}
          className="text-xs font-medium text-text-primary hover:text-accent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPortalLoading
            ? "Opening billing…"
            : `Manage billing — ${tier.tier === "solo" ? "Solo" : tier.tier === "band" ? "Band" : "Free"} plan`}
        </button>
      </div>

      {/* Band name */}
      <div className="mb-4 rounded-md border border-border bg-surface p-4">
        {user.isOwner ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={renameBandValue}
              onChange={(e) => setRenameBandValue(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  renameBandValue.trim() &&
                  renameBandValue.trim() !== band.name &&
                  !isRenamingBand
                ) {
                  handleRenameBand();
                }
              }}
              disabled={isRenamingBand}
              maxLength={50}
              className="h-8 flex-1 rounded border border-subtle bg-surface px-2 text-sm font-medium text-text-primary focus:border-accent-blue focus:outline-none focus:ring-1 focus:ring-accent-blue/40 disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleRenameBand}
              disabled={isRenamingBand || !renameBandValue.trim() || renameBandValue.trim() === band.name}
              className="shrink-0 rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isRenamingBand ? "Saving…" : "Save"}
            </button>
          </div>
        ) : (
          <h2 className="text-sm font-medium text-text-primary">{band.name}</h2>
        )}
      </div>

      {/* Members table */}
      <div className="rounded-md border border-border bg-surface p-4">
        <h2 className="mb-4 text-sm font-medium text-text-primary">Members</h2>

        {membersError && <p className="mb-3 text-sm text-danger">{membersError}</p>}

        {isMembersLoading ? (
          <p className="text-sm text-text-secondary">Loading members...</p>
        ) : members.length === 0 ? (
          <p className="text-sm text-text-secondary">No members found.</p>
        ) : (
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-text-secondary">
                <th className="w-[45%] pb-2 pr-4 font-medium">Name</th>
                <th className="w-[25%] pb-2 font-medium">Role</th>
                <th className="w-[30%] pb-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const isEditing = editingMemberUid === m.uid;
                const canEdit = user.role === "admin" || user.uid === m.uid;

                return (
                  <tr key={m.uid} className="group h-[48px] border-b border-border last:border-0">
                    {isEditing ? (
                      <>
                        <td className="align-middle py-1.5 pr-3">
                          <input
                            type="text"
                            value={editingMemberName}
                            onChange={(e) => setEditingMemberName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveMember(m.uid);
                              if (e.key === "Escape") cancelEdit();
                            }}
                            className="block h-7 w-full max-w-full rounded border border-accent-blue/40 bg-surface px-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-blue/40"
                            style={{ width: "100%", boxSizing: "border-box" }}
                            autoFocus
                            disabled={isSavingMember}
                          />
                        </td>
                        <td className="align-middle w-[25%] py-1.5 pr-3">
                          {user.role === "admin" ? (
                            <select
                              value={editingMemberRole}
                              onChange={(e) => setEditingMemberRole(e.target.value)}
                              disabled={isSavingMember}
                              className="h-7 rounded border border-subtle bg-surface px-2 text-xs text-text-secondary focus:border-accent-blue focus:outline-none focus:ring-1 focus:ring-accent-blue/40"
                            >
                              <option value="member">Member</option>
                              <option value="admin">Admin</option>
                            </select>
                          ) : (
                            <span className="capitalize text-text-secondary">{m.role}</span>
                          )}
                        </td>
                        <td className="align-middle w-[30%] py-1.5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleSaveMember(m.uid)}
                              disabled={isSavingMember}
                              className="text-xs text-accent hover:underline disabled:opacity-50"
                            >
                              {isSavingMember ? "Saving…" : "Save"}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              disabled={isSavingMember}
                              className="text-xs text-text-muted hover:text-text-primary"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="align-middle py-2 pr-4 text-text-primary">{m.displayName}</td>
                        <td className="align-middle w-[25%] py-2 capitalize text-text-secondary">{m.role}</td>
                        <td className="align-middle w-[30%] py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {user.isOwner && m.uid !== user.uid && (
                              <button
                                type="button"
                                onClick={() => handleTransferOwnership(m)}
                                className="text-xs text-text-muted hover:text-text-secondary"
                              >
                                Transfer ownership
                              </button>
                            )}
                            {canEdit && (
                              <button
                                type="button"
                                aria-label="Edit member"
                                onClick={() => startEdit(m)}
                                className="border-0 bg-transparent p-0.5 text-text-muted hover:text-text-primary"
                              >
                                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <path d="M11.5 2.5a1.414 1.414 0 0 1 2 2L5 13H3v-2L11.5 2.5z" />
                                </svg>
                              </button>
                            )}
                            {user.role === "admin" && m.role === "member" && m.uid !== user.uid && (
                              <button
                                type="button"
                                aria-label="Remove member"
                                disabled={removingUid === m.uid}
                                onClick={() => handleRemoveMember(m)}
                                className="border-0 bg-transparent p-0.5 text-danger hover:text-danger disabled:opacity-40"
                              >
                                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <path d="M6 2h4M2 4h12M5 4l.5 8.5a1 1 0 0 0 1 .95h3a1 1 0 0 0 1-.95L11 4" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {user.role === "admin" && (
        <InviteHistory
          invites={invites}
          invitesError={invitesError}
          hasMoreInvites={hasMoreInvites}
          onLoadMore={() => fetchInvites({ loadMore: true })}
        />
      )}

      {/* Danger zone */}
      <div className="mt-4 rounded-md border border-danger/30 bg-surface p-4">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-danger">Danger zone</p>
        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-text-secondary">
            Permanently delete your account and all your data. This cannot be undone.
          </p>
          <button
            type="button"
            className="shrink-0 rounded border border-danger/50 px-3 py-1.5 text-xs font-medium text-danger hover:bg-danger/10"
            onClick={() => {
              requestConfirm({
                title: "Delete your account",
                message: "This will permanently delete your account, remove you from all bands, and cannot be undone.",
                confirmLabel: "Delete account",
                danger: true,
                onConfirm: async () => {
                  onSetDeletingAccount(true);
                  try {
                    const deleteAccountFn = httpsCallable(functions, "deleteAccount");
                    await deleteAccountFn();
                    dismissConfirm();
                    await signOut(auth);
                    navigate("/");
                  } catch (err) {
                    if (
                      err.code === "functions/failed-precondition" &&
                      err.details?.bandNames?.length
                    ) {
                      dismissConfirm();
                      showToast(`Transfer or dissolve these bands first: ${err.details.bandNames.join(", ")}`);
                    } else {
                      showToast("Could not delete account. Please try again.");
                    }
                  } finally {
                    onSetDeletingAccount(false);
                  }
                },
              });
            }}
          >
            Delete account
          </button>
        </div>
      </div>

      <DowngradeConfirmModal
        open={showDowngradeModal}
        currentTier={tier.tier}
        songCount={songs.songs.length}
        setlistCount={setlists.setlists.length}
        memberCount={members.length}
        onCancel={() => setShowDowngradeModal(false)}
        onConfirm={() => { setShowDowngradeModal(false); openBillingPortal(); }}
      />
    </section>
  );
}
